import { atom } from 'jotai';
import { RESET, atomWithReset } from 'jotai/utils';

import { coordinatedAtom, stepAtom } from '../../../shared/atoms/utils/';
import { DeliveryMethod } from '../DeliveryMethod';
import { launchSettingAtom } from './launchSettingAtom';

export const quizIdAtom = atomWithReset<number>(1);
quizIdAtom.onMount = (set) => () => set(RESET);

export const quizNameAtom = atomWithReset<string>('');
quizNameAtom.onMount = (set) => () => set(RESET);

export const workspaceQuizIdAtom = atomWithReset<string>('');
workspaceQuizIdAtom.onMount = (set) => () => set(RESET);

export const workspaceIdAtom = atomWithReset<string | null>('');
workspaceIdAtom.onMount = (set) => () => set(RESET);

export const workspaceNameAtom = atomWithReset<string>('');
workspaceNameAtom.onMount = (set) => () => set(RESET);

export const quizTypeAtom = atomWithReset<'Workspace' | 'Quiz' | null>(null);
quizTypeAtom.onMount = (set) => () => set(RESET);

export const randomQuestionsAtom = launchSettingAtom({
  [DeliveryMethod.OpenNavigation]: false,
  [DeliveryMethod.InstantFeedback]: false,
  [DeliveryMethod.SpaceRace]: false,
  [DeliveryMethod.TeacherPaced]: false,
  rosteredRoomSetting: false,
});

export const showResultsAtom = launchSettingAtom({
  [DeliveryMethod.OpenNavigation]: false,
  [DeliveryMethod.InstantFeedback]: false,
  [DeliveryMethod.SpaceRace]: false,
  [DeliveryMethod.TeacherPaced]: false,
  rosteredRoomSetting: false,
});

export const oneAttemptAtom = launchSettingAtom({
  [DeliveryMethod.OpenNavigation]: false,
  [DeliveryMethod.InstantFeedback]: false,
  [DeliveryMethod.SpaceRace]: false,
  [DeliveryMethod.TeacherPaced]: false,
  rosteredRoomSetting: true,
});

export const showFeedbackAtom = launchSettingAtom({
  [DeliveryMethod.OpenNavigation]: false,
  [DeliveryMethod.InstantFeedback]: true,
  [DeliveryMethod.SpaceRace]: true,
  [DeliveryMethod.TeacherPaced]: false,
  rosteredRoomSetting: false,
});

export const requireNamesAtom = launchSettingAtom({
  [DeliveryMethod.OpenNavigation]: true,
  [DeliveryMethod.InstantFeedback]: true,
  [DeliveryMethod.SpaceRace]: true,
  [DeliveryMethod.TeacherPaced]: true,
  rosteredRoomSetting: true,
});

export const randomAnswersAtom = launchSettingAtom({
  [DeliveryMethod.OpenNavigation]: false,
  [DeliveryMethod.InstantFeedback]: false,
  [DeliveryMethod.SpaceRace]: false,
  [DeliveryMethod.TeacherPaced]: false,
  rosteredRoomSetting: false,
});

// Timer: -1 means no timer, otherwise it's the duration in seconds (60-7200)
// Defaults to disabled (-1) for all delivery methods
export const quizTimerSecondsAtom = launchSettingAtom({
  [DeliveryMethod.OpenNavigation]: -1,
  [DeliveryMethod.InstantFeedback]: -1,
  [DeliveryMethod.SpaceRace]: -1,
  [DeliveryMethod.TeacherPaced]: -1,
  rosteredRoomSetting: -1,
});

export const deliveryMethodAtom = coordinatedAtom<
  DeliveryMethod,
  boolean,
  [deliveryMethod: DeliveryMethod, isRoomRostered?: boolean]
>(DeliveryMethod.InstantFeedback, [
  oneAttemptAtom,
  requireNamesAtom,
  randomAnswersAtom,
  randomQuestionsAtom,
  showFeedbackAtom,
  showResultsAtom,
  quizTimerSecondsAtom,
]);

export const launchDialogStepAtom = stepAtom({ initial: 1 });
launchDialogStepAtom.onMount = (set) => {
  set(1);
};

export const noBackButtonAtom = atom<boolean>(false);
noBackButtonAtom.onMount = (set) => {
  set(false);
};

export const isActivityStartingAtom = atom<boolean>(false);
isActivityStartingAtom.onMount = (set) => {
  set(false);
};

const _isLaunchWorkspaceQuizDialogOpenAtom = atom(false);
_isLaunchWorkspaceQuizDialogOpenAtom.onMount = (set) => {
  set(false);
};

export const isLaunchWorkspaceQuizDialogOpenAtom = atom(
  (get) => get(_isLaunchWorkspaceQuizDialogOpenAtom),
  (get, set, args: LaunchWorkspaceQuizDialogAction) => {
    switch (args.type) {
      case 'open': {
        const { workspaceQuizId, workspaceId, quizName } = args.payload;
        set(_isLaunchWorkspaceQuizDialogOpenAtom, true);
        set(quizTypeAtom, 'Workspace');
        set(quizIdAtom, RESET);
        set(workspaceQuizIdAtom, workspaceQuizId);
        set(workspaceIdAtom, workspaceId);
        set(quizNameAtom, quizName);
        break;
      }
      case 'close': {
        set(_isLaunchWorkspaceQuizDialogOpenAtom, false);
        set(quizTypeAtom, RESET);
        set(quizIdAtom, RESET);
        set(workspaceQuizIdAtom, RESET);
        set(workspaceIdAtom, RESET);
        set(quizNameAtom, RESET);
        break;
      }
    }
  }
);

type LaunchWorkspaceQuizDialogAction =
  | {
      type: 'open';
      payload: {
        workspaceQuizId: string;
        workspaceId: string;
        quizName: string;
      };
    }
  | { type: 'close' };

export type LaunchWorkspaceQuizDialogActionSetter = (
  args: LaunchWorkspaceQuizDialogAction
) => void;
